/**
 * Toast.
 *
 * One at a time, bottom centre, four seconds, and it carries confirmations
 * only. There is no error tone and there will not be one: a message a person
 * has to read is a message that must not be able to leave on its own, so
 * errors are owned by the page through the error block. Making the wrong thing
 * impossible to build is cheaper than a review comment that says do not.
 *
 * A second toast replaces the first rather than stacking. A stack is a queue
 * the reader did not ask to join.
 *
 * A copy never toasts. The copy field's own check swap is the receipt, and a
 * toast on top of it is noise, per screen spec 4.7.
 */

import { useSyncExternalStore } from 'react';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/toast.css';

export interface ToastMessage {
  id: number;
  text: string;
  /** A mono literal shown under the sentence. A name, never a sentence. */
  detail?: string;
}

let current: ToastMessage | null = null;
let nextId = 1;
let timer: number | undefined;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot() {
  return current;
}

export const TOAST_MS = 4000;

export function showToast(text: string, detail?: string): void {
  window.clearTimeout(timer);
  current = { id: nextId++, text, detail };
  captureEvent('toast_shown', {
    text,
    detail,
    screen_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
  emit();
  timer = window.setTimeout(() => {
    current = null;
    emit();
  }, TOAST_MS);
}

export function dismissToast(): void {
  window.clearTimeout(timer);
  current = null;
  emit();
}

export function ToastHost() {
  const toast = useSyncExternalStore(subscribe, snapshot, snapshot);

  return (
    <div className="toast-host" role="status" aria-live="polite">
      {toast ? (
        <div className="toast" key={toast.id}>
          <span className="toast__text">{toast.text}</span>
          {toast.detail ? <span className="toast__detail mono">{toast.detail}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
